import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useAuth } from './AuthContext'

interface CreditBalance {
  creditBalance: number
  freeTrialUsed: boolean
  totalPurchased: number
  totalUsed: number
}

interface CreditPackage {
  packageId: string
  name: string
  credits: number
  priceAUD: number
  pricePerCredit: string
  popular: boolean
  description: string
  features: string[]
  ctaText: string
}

interface CreditsContextType {
  credits: CreditBalance | null
  packages: CreditPackage[]
  loading: boolean
  error: string | null
  fetchCredits: () => Promise<void>
  fetchPackages: () => Promise<void>
  checkCredits: () => Promise<boolean>
  claimFreeTrial: () => Promise<boolean>
  createCheckoutSession: (packageId: string) => Promise<string | null>
}

const CreditsContext = createContext<CreditsContextType | undefined>(undefined)

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

export const CreditsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth()
  const [credits, setCredits] = useState<CreditBalance | null>(null)
  const [packages, setPackages] = useState<CreditPackage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const getAuthToken = (): string | null => {
    const storedTokens = localStorage.getItem('authTokens')
    return storedTokens ? JSON.parse(storedTokens).idToken : null
  }

  const fetchCredits = async () => {
    if (!isAuthenticated || !user?.id) return

    setLoading(true)
    setError(null)

    try {
      const token = getAuthToken()
      const response = await fetch(
        `${API_BASE_URL}/api/credits?userId=${user.id}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      )

      const data = await response.json()

      if (data.success) {
        // Check if this is a new user (no credits record yet) - auto-claim free trial
        if (data.createdAt === null && !data.freeTrialUsed) {
          console.log('New user detected, claiming free trial...')
          const trialClaimed = await claimFreeTrial()
          if (trialClaimed) {
            return // claimFreeTrial will call fetchCredits again
          }
        }
        
        setCredits({
          creditBalance: data.creditBalance,
          freeTrialUsed: data.freeTrialUsed,
          totalPurchased: data.totalPurchased,
          totalUsed: data.totalUsed
        })
      } else {
        setError(data.error || 'Failed to fetch credits')
      }
    } catch (err) {
      setError('Failed to fetch credits')
      console.error('Error fetching credits:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchPackages = async () => {
    try {
      const token = getAuthToken()
      const response = await fetch(`${API_BASE_URL}/api/credits/packages`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      })

      const data = await response.json()

      if (data.success) {
        setPackages(data.packages)
      }
    } catch (err) {
      console.error('Error fetching packages:', err)
    }
  }

  const checkCredits = async (): Promise<boolean> => {
    if (!isAuthenticated || !user?.id) return false

    try {
      const token = getAuthToken()
      const response = await fetch(`${API_BASE_URL}/api/credits/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ userId: user.id })
      })

      const data = await response.json()
      return data.hasCredits || false
    } catch (err) {
      console.error('Error checking credits:', err)
      return false
    }
  }

  const claimFreeTrial = async (): Promise<boolean> => {
    if (!isAuthenticated || !user?.id) return false

    try {
      const token = getAuthToken()
      const response = await fetch(`${API_BASE_URL}/api/credits/free-trial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ userId: user.id })
      })

      const data = await response.json()

      if (data.success) {
        // Update credits state directly to avoid recursive fetch
        setCredits({
          creditBalance: data.creditBalance,
          freeTrialUsed: true,
          totalPurchased: 0,
          totalUsed: 0
        })
        return true
      }
      return false
    } catch (err) {
      console.error('Error claiming free trial:', err)
      return false
    }
  }

  const createCheckoutSession = async (packageId: string): Promise<string | null> => {
    if (!isAuthenticated || !user?.id) return null

    try {
      const token = getAuthToken()
      const response = await fetch(`${API_BASE_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({
          userId: user.id,
          packageId,
          email: user.email
        })
      })

      const data = await response.json()

      if (data.success && data.checkoutUrl) {
        return data.checkoutUrl
      }
      return null
    } catch (err) {
      console.error('Error creating checkout session:', err)
      return null
    }
  }

  // Fetch credits when user logs in
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchCredits()
    } else {
      setCredits(null)
    }
  }, [isAuthenticated, user?.id])

  return (
    <CreditsContext.Provider
      value={{
        credits,
        packages,
        loading,
        error,
        fetchCredits,
        fetchPackages,
        checkCredits,
        claimFreeTrial,
        createCheckoutSession
      }}
    >
      {children}
    </CreditsContext.Provider>
  )
}

export const useCredits = (): CreditsContextType => {
  const context = useContext(CreditsContext)
  if (context === undefined) {
    throw new Error('useCredits must be used within a CreditsProvider')
  }
  return context
}
